import { Mark, mergeAttributes } from "@tiptap/core";

// Legacy <font size="N"> (1-7), NOT the official CSS-based font-size extension. This is a
// deliberate compatibility shim, not a stylistic choice — existing saved exam content already
// uses <font size> (written by the old editor's execCommand("fontSize", ...) calls), and the
// storage format is a hard constraint: this migration does not touch what's already saved.
// Do not swap this for style="font-size:...px" without a real, explicit data-migration decision.
//
// Reading is wider than writing, for the same reason as FontColor: everything outside this
// editor sizes text with CSS, so a pasted size used to vanish. CSS sizes are mapped onto the
// nearest legacy bucket on the way in, which leaves the stored format untouched.

/** Legacy sizes 1-7 against their rendered pixel sizes at a 16px base. */
const LEGACY_PIXELS: Array<[string, number]> = [
  ["1", 10], ["2", 13], ["3", 16], ["4", 18], ["5", 24], ["6", 32], ["7", 48],
];

function toPixels(value: string): number | null {
  const match = /^([0-9.]+)\s*(px|pt|em|rem)?$/i.exec(value.trim());
  if (!match) return null;
  const amount = Number(match[1]);
  if (!isFinite(amount) || amount <= 0) return null;
  switch ((match[2] || "px").toLowerCase()) {
    case "pt": return amount * (4 / 3);
    case "em":
    case "rem": return amount * 16;
    default: return amount;
  }
}

/** Nearest bucket, or null when the size is what the text would have been anyway. */
function toLegacySize(value: string): string | null {
  const pixels = toPixels(value);
  if (pixels == null) return null;

  let best = LEGACY_PIXELS[0];
  for (const candidate of LEGACY_PIXELS) {
    if (Math.abs(candidate[1] - pixels) < Math.abs(best[1] - pixels)) best = candidate;
  }
  // Word and Docs put a font-size on nearly every element. Mapping those to the default
  // bucket would wrap a whole pasted document in markup that changes nothing.
  return best[0] === "3" ? null : best[0];
}

export const FontSize = Mark.create({
  name: "fontSize",

  addAttributes() {
    return {
      size: {
        default: "3",
        // See FontColor: only the element case reaches here, because the style rule below
        // supplies the mapped bucket itself.
        parseHTML: (el: HTMLElement) => el?.getAttribute?.("size") || "3",
        renderHTML: (attrs: { size: string }) => ({ size: attrs.size }),
      },
    };
  },

  parseHTML() {
    return [
      { tag: "font[size]" },
      {
        style: "font-size",
        getAttrs: (value: string) => {
          const size = toLegacySize(String(value));
          return size ? { size } : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["font", mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setFontSize:
        (size: string) =>
        ({ commands }: any) =>
          commands.setMark(this.name, { size }),
      unsetFontSize:
        () =>
        ({ commands }: any) =>
          commands.unsetMark(this.name),
    } as any;
  },
});
